<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useContentClipboard } from '@/modules/content/composables/content-clipboard'

// Mounted once at the app root. Renders only when the clipboard holds an
// element (i.e. after a copy/cut in the content designer), so it's invisible
// everywhere else. Shows what's on the clipboard and lets the user clear it —
// the only way to leave "copy" mode, since copy is kept for repeated pastes.
const { t } = useI18n()
const { entry, hasContent, mode, clear } = useContentClipboard()

const elementName = computed(() => entry.value?.element.name ?? '')
const icon = computed(() => (mode.value === 'cut' ? 'mdi-content-cut' : 'mdi-content-copy'))
const modeLabel = computed(() =>
  mode.value === 'cut' ? t('content.clipboard.cut') : t('content.clipboard.copied')
)
</script>

<template>
  <Transition name="clipboard-indicator">
    <v-card v-if="hasContent" class="content-clipboard-indicator" rounded="lg" elevation="8">
      <div class="d-flex align-center ga-3 pa-3">
        <v-icon :icon="icon" color="primary" />
        <div class="d-flex flex-column overflow-hidden">
          <span class="text-caption text-medium-emphasis">{{ modeLabel }}</span>
          <span class="text-body-2 font-weight-medium text-truncate">
            {{ elementName }}
          </span>
          <span class="text-caption text-medium-emphasis text-truncate">
            {{ t('content.clipboard.hint') }}
          </span>
        </div>
        <v-btn
          icon="mdi-close"
          size="small"
          variant="text"
          :title="t('content.clipboard.clear')"
          @click="clear"
        />
      </div>
    </v-card>
  </Transition>
</template>

<style scoped lang="scss">
.content-clipboard-indicator {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  width: 320px;
  max-width: calc(100vw - 32px);
}

// Animate opacity only so the centering translateX isn't disturbed.
.clipboard-indicator-enter-active,
.clipboard-indicator-leave-active {
  transition: opacity 0.2s ease;
}

.clipboard-indicator-enter-from,
.clipboard-indicator-leave-to {
  opacity: 0;
}
</style>
